import React, { useCallback, useEffect, useRef, useState } from 'react';
import AutorenewOutlinedIcon from '@mui/icons-material/AutorenewOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  InvoicePdfRequestError,
  strapiDownloadWorkOrderInvoicePdf,
  strapiOpenWorkOrderInvoicePdfHtmlPreview,
} from '../../api/strapiCrm';

type Props = {
  open: boolean;
  workOrderDocumentId: string | null;
  workOrderLabel?: string | null;
  jwt: string | null;
  onClose: () => void;
  /** Proforma menyertakan baris DP dan sisa pembayaran. */
  variant?: 'invoice' | 'proforma';
  onResetProformaAmount?: () => void;
  resettingProformaAmount?: boolean;
  /** Return true bila error ditangani di luar dialog (misalnya dikembalikan ke form jumlah bayar). */
  onLoadError?: (message: string, grandTotal: number | null) => boolean;
};

export function InvoicePdfPreviewDialog({
  open,
  workOrderDocumentId,
  workOrderLabel,
  jwt,
  onClose,
  variant = 'invoice',
  onResetProformaAmount,
  resettingProformaAmount = false,
  onLoadError,
}: Props) {
  const onLoadErrorRef = useRef(onLoadError);
  onLoadErrorRef.current = onLoadError;
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [openingHtml, setOpeningHtml] = useState(false);

  const loadPdf = useCallback(
    async (opts?: { keepPreview?: boolean }) => {
      if (!workOrderDocumentId || !jwt) return;
      setLoading(true);
      setError(null);
      if (!opts?.keepPreview) {
        setPdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
      }
      try {
        const blob = await strapiDownloadWorkOrderInvoicePdf(jwt, workOrderDocumentId, {
          cacheBust: Boolean(opts?.keepPreview),
          variant,
        });
        setPdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Gagal memuat PDF invoice.';
        const grandTotal = e instanceof InvoicePdfRequestError ? e.grandTotal : null;
        if (onLoadErrorRef.current?.(message, grandTotal)) return;
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [jwt, variant, workOrderDocumentId],
  );

  useEffect(() => {
    if (!open || !workOrderDocumentId || !jwt) return undefined;
    void loadPdf();
    return undefined;
  }, [open, workOrderDocumentId, jwt, loadPdf]);

  useEffect(() => {
    if (open) return undefined;
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setError(null);
    setLoading(false);
    setRegenerating(false);
    setOpeningHtml(false);
    return undefined;
  }, [open]);

  async function handleRegenerate() {
    if (!workOrderDocumentId || !jwt || loading || regenerating) return;
    setRegenerating(true);
    try {
      await loadPdf({ keepPreview: true });
    } finally {
      setRegenerating(false);
    }
  }

  async function handleOpenHtmlPreview() {
    if (!workOrderDocumentId || !jwt || loading || regenerating || openingHtml) return;
    setOpeningHtml(true);
    setError(null);
    try {
      await strapiOpenWorkOrderInvoicePdfHtmlPreview(jwt, workOrderDocumentId, { variant });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal membuka preview HTML invoice.');
    } finally {
      setOpeningHtml(false);
    }
  }

  function handleDownload() {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    const safe = (workOrderLabel?.trim() || workOrderDocumentId || 'Invoice').replace(/[^\w.-]+/g, '_');
    a.download = variant === 'proforma' ? 'Proforma.pdf' : `${safe}_Invoice.pdf`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  const titleLabel =
    variant === 'proforma' ? 'Proforma pdf' : workOrderLabel?.trim() || workOrderDocumentId || 'Invoice';
  const dialogTitle = variant === 'proforma' ? 'Proforma pdf' : `PDF Invoice — ${titleLabel}`;
  const pdfBusy = loading || regenerating;
  const busy = pdfBusy || openingHtml;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      aria-labelledby="invoice-pdf-preview-title"
      data-testid="invoice-pdf-preview-dialog"
    >
      <DialogTitle id="invoice-pdf-preview-title" sx={{ pr: 6 }}>
        {dialogTitle}
        <IconButton
          aria-label="Tutup"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: '20px',
          height: { xs: '70vh', md: '80vh' },
          bgcolor: 'grey.100',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {error ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : null}
        {!error && pdfBusy && !pdfUrl ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', gap: 2, p: 3 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              {regenerating ? 'Menggenerate ulang PDF invoice…' : 'Menyiapkan PDF invoice…'}
            </Typography>
          </Stack>
        ) : null}
        {!error && pdfUrl ? (
          <Box sx={{ position: 'relative', width: '100%', flex: 1, minHeight: 0 }}>
            {pdfBusy ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 2,
                  bgcolor: 'rgba(255,255,255,0.72)',
                  gap: 1,
                }}
              >
                <CircularProgress size={28} />
                <Typography variant="body2" color="text.secondary">
                  Menggenerate ulang PDF invoice…
                </Typography>
              </Stack>
            ) : null}
            <Box
              component="iframe"
              src={pdfUrl}
              title={dialogTitle}
              sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
            />
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, gap: 1, flexWrap: 'wrap' }}>
        {variant === 'proforma' && onResetProformaAmount ? (
          <Button
            color="warning"
            onClick={onResetProformaAmount}
            disabled={!workOrderDocumentId || !jwt || busy || resettingProformaAmount}
            data-testid="invoice-proforma-reset-amount"
          >
            {resettingProformaAmount ? 'Mereset…' : 'Reset jumlah bayar'}
          </Button>
        ) : null}
        <Button
          onClick={() => void handleRegenerate()}
          disabled={!workOrderDocumentId || !jwt || busy}
          startIcon={regenerating ? <CircularProgress size={16} color="inherit" /> : <AutorenewOutlinedIcon />}
        >
          Generate ulang
        </Button>
        <Button
          onClick={() => void handleOpenHtmlPreview()}
          disabled={!workOrderDocumentId || !jwt || busy}
          startIcon={openingHtml ? <CircularProgress size={16} color="inherit" /> : <OpenInNewOutlinedIcon />}
        >
          Preview HTML
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleDownload} disabled={!pdfUrl || busy} startIcon={<DownloadOutlinedIcon />}>
          Unduh PDF
        </Button>
        <Button onClick={onClose}>Tutup</Button>
      </DialogActions>
    </Dialog>
  );
}
